/**
 * Task List View Component
 * Alternative table view for tasks (instead of Kanban)
 */

import { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { useTasks } from '../hooks/useTasks';
import { TaskStatusBadge } from './TaskStatusBadge';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { TaskDetailModal } from './TaskDetailModal';
import { formatDate } from '../utils/dateUtils';
import type { KanbanQueryParams, TaskListItem } from '../types';
import type { QuoteOption } from '../api';

interface TaskListViewProps {
  filters: KanbanQueryParams;
  quotes?: QuoteOption[];
  assignees?: Array<{ id: string; name: string; email?: string }>;
  canEditTasks?: boolean;
  canDeleteTasks?: boolean;
  canAssignTasks?: boolean;
}

type SortKey = 'task_number' | 'title' | 'status' | 'priority' | 'assigned_to_name' | 'due_date' | 'created_at';

export function TaskListView({
  filters,
  quotes = [],
  assignees = [],
  canEditTasks = true,
  canDeleteTasks = false,
  canAssignTasks = false,
}: TaskListViewProps) {
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedTask, setSelectedTask] = useState<TaskListItem | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  const { data, isLoading, error } = useTasks({
    ...filters,
    pageNumber,
    pageLength: 25,
    sortKey,
    sortDirection,
  });

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDirection((prev) => (prev === 1 ? -1 : 1));
      } else {
        setSortKey(key);
        setSortDirection(-1);
      }
    },
    [sortKey]
  );

  const handleTaskClick = useCallback((task: TaskListItem) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  }, []);

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 1 ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Failed to load tasks. Please try again.
      </div>
    );
  }

  if (!data || data.tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No tasks found
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('task_number')}
              >
                <div className="flex items-center gap-1">
                  #
                  <SortIcon columnKey="task_number" />
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center gap-1">
                  Title
                  <SortIcon columnKey="title" />
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  <SortIcon columnKey="status" />
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('priority')}
              >
                <div className="flex items-center gap-1">
                  Priority
                  <SortIcon columnKey="priority" />
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('assigned_to_name')}
              >
                <div className="flex items-center gap-1">
                  Assignee
                  <SortIcon columnKey="assigned_to_name" />
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('due_date')}
              >
                <div className="flex items-center gap-1">
                  Due Date
                  <SortIcon columnKey="due_date" />
                </div>
              </TableHead>
              <TableHead>Tags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.tasks.map((task) => (
              <TableRow
                key={task.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleTaskClick(task)}
              >
                <TableCell className="text-xs text-muted-foreground">
                  {(() => {
                    const quote = task.quote_id ? quotes.find((q) => q.id === task.quote_id) : null;
                    const quoteNumber = quote?.quote_number;
                    if (quoteNumber) {
                      return <span className="font-mono">#{quoteNumber}</span>;
                    }
                    return (
                      <span className="text-muted-foreground/50 italic">
                        No quote
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="font-medium max-w-[300px] truncate">
                  {task.title}
                </TableCell>
                <TableCell>
                  <TaskStatusBadge status={task.status} />
                </TableCell>
                <TableCell>
                  <TaskPriorityBadge priority={task.priority} />
                </TableCell>
                <TableCell className="text-sm">
                  {task.assigned_to_name || (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {task.due_date ? (
                    formatDate(task.due_date)
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {task.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {task.tags.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{task.tags.length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Page {data.pagination.pageNumber} of {data.pagination.totalPages} ({data.pagination.totalRecords} tasks)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              disabled={pageNumber === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={pageNumber >= data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={showTaskDetail}
        onOpenChange={setShowTaskDetail}
        taskId={selectedTask?.id}
        taskPreview={selectedTask ?? undefined}
        quotes={quotes}
        assignees={assignees}
        canEditTasks={canEditTasks}
        canDeleteTasks={canDeleteTasks}
        canAssignTasks={canAssignTasks}
      />
    </>
  );
}
